import type { ChildApp } from "./appList";
import type { AppModule } from "./contract";

// Thrown only when the module itself could not be fetched (403 on an expired
// link, network error) -- the one case a fresh link can fix.
export class AppImportError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = "AppImportError";
  }
}

// The app's signed link had expired and a fresh one could not be used either;
// a full page reload (new sign-in check, new app list) fixes it.
export class AppLinkExpiredError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = "AppLinkExpiredError";
  }
}

// The module downloaded but is not an app this host can run (missing
// mount/meta, or another contractVersion). A retry will not help.
export class AppContractError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AppContractError";
  }
}

// The entry URL is outside the artifact allow-list, so it was never imported.
// Needs a configuration fix, not a retry.
export class AppBlockedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AppBlockedError";
  }
}

// An import() runs the module in AMP's own browser context, so only same-origin
// paths, localhost in dev, and the configured artifact container(s) may load.
// REACT_APP_CHILD_APP_ARTIFACT_URLS is a comma-separated list of URL prefixes,
// e.g. https://<account>.blob.core.windows.net/ui-artifacts/
const artifactPrefixes = () =>
  (process.env.REACT_APP_CHILD_APP_ARTIFACT_URLS ?? "")
    .split(",")
    .map((prefix) => prefix.trim())
    .filter(Boolean);

export function isAllowedEntry(entry: string, prefixes: string[] = artifactPrefixes()) {
  let url: URL;
  try {
    url = new URL(entry, window.location.origin);
  } catch {
    return false;
  }
  if (url.origin === window.location.origin) return true;
  if (url.protocol === "https:" && prefixes.some((prefix) => url.href.startsWith(prefix)))
    return true;
  return (
    process.env.NODE_ENV === "development" &&
    (url.hostname === "localhost" || url.hostname === "127.0.0.1")
  );
}

// The first entry seen per app is kept for the life of the page: import() of
// the exact URL that was preloaded is served from memory, whereas a newly
// issued link is a different module and downloads again. Only an import retry
// replaces it.
const pinnedEntries = new Map<string, string>();
const preloaded = new Set<string>();

export function entryFor(app: ChildApp) {
  if (!pinnedEntries.has(app.slug)) pinnedEntries.set(app.slug, app.entry);
  return pinnedEntries.get(app.slug)!;
}

export function repinEntry(app: ChildApp) {
  pinnedEntries.set(app.slug, app.entry);
  return app.entry;
}

// Test hook: forget every pinned and preloaded entry.
export function resetPinnedEntries() {
  pinnedEntries.clear();
  preloaded.clear();
}

// Downloads and parses every app as soon as the list arrives, without running
// it. onResult reports each download (a 403, network or CORS failure fires the
// link's error event) so an app can be marked unhealthy before anyone opens it.
export function preloadApps(
  apps: ChildApp[],
  onResult?: (app: ChildApp, ok: boolean) => void,
) {
  for (const app of apps) {
    const href = new URL(entryFor(app), window.location.origin).href;
    if (preloaded.has(href)) continue;
    if (!isAllowedEntry(href)) {
      onResult?.(app, false);
      continue;
    }
    preloaded.add(href);
    const link = document.createElement("link");
    link.rel = "modulepreload";
    link.href = href;
    link.addEventListener("load", () => onResult?.(app, true));
    link.addEventListener("error", () => onResult?.(app, false));
    document.head.appendChild(link);
  }
}

// True once a signed link is past its SAS expiry (the se= parameter). Links
// without one (same-origin and local dev builds) never expire.
export function isLinkExpired(entry: string, now: number = Date.now()) {
  let expiry: string | null;
  try {
    expiry = new URL(entry, window.location.origin).searchParams.get("se");
  } catch {
    return false;
  }
  const expiresAt = expiry ? Date.parse(expiry) : NaN;
  return !Number.isNaN(expiresAt) && now >= expiresAt;
}

export type Importer = (url: string) => Promise<unknown>;

// webpackIgnore keeps webpack from trying to bundle a runtime URL.
const defaultImporter: Importer = (url) => import(/* webpackIgnore: true */ url);

export async function loadApp(entry: string, importer: Importer = defaultImporter): Promise<AppModule> {
  const resolvedEntry = new URL(entry, window.location.origin).href;
  if (!isAllowedEntry(resolvedEntry))
    throw new AppBlockedError("App entry is not on AMP's artifact allow-list");

  // Child bundles read process.env.NODE_ENV at runtime; the browser has no `process`.
  const runtime = globalThis as unknown as { process?: unknown };
  if (!runtime.process) runtime.process = { env: { NODE_ENV: process.env.NODE_ENV ?? "production" } };

  let module: AppModule;
  try {
    module = (await importer(resolvedEntry)) as AppModule;
  } catch (error) {
    throw new AppImportError("This app could not be downloaded", { cause: error });
  }
  if (!module || typeof module.mount !== "function" || !module.meta)
    throw new AppContractError("App entry does not implement the child-app contract");
  if (module.meta.contractVersion !== 1)
    throw new AppContractError(
      `App contract version ${String(module.meta.contractVersion)} is not supported (need 1)`,
    );
  return module;
}

// Load an app, and on a download failure fetch the list once more and retry
// with the fresh link (an expired link is the usual cause). refresh() returns
// the app's current registration, or undefined if the user no longer has it.
export async function loadAppWithRetry(
  app: ChildApp,
  refresh: () => Promise<ChildApp | undefined>,
  importer?: Importer,
): Promise<AppModule> {
  try {
    return await loadApp(entryFor(app), importer);
  } catch (error) {
    if (!(error instanceof AppImportError)) throw error;
    const fresh = await refresh();
    if (!fresh) throw error;
    if (isLinkExpired(fresh.entry))
      throw new AppLinkExpiredError("This app's link expired and could not be renewed", {
        cause: error,
      });
    return loadApp(repinEntry(fresh), importer);
  }
}
